import { createThunk, model } from 'mst-collection';
import {
  getParent,
  types,
  castToSnapshot,
  getRoot,
  IModelType,
  getType,
} from 'mobx-state-tree';

import {
  schemas,
  Api,
  ICommentResponse,
  IUserPublicResponse,
  ICreateCommentParams,
} from '@api';
import {
  ExtendedListModel,
  handleFlow,
  shouldRejectPagination,
  getCommentApiByParentEntity,
} from '../utils';
import {
  CommentModel,
  Comment,
  CommentRefModel,
} from './CommentModel';
import { trim } from '@utils';
import type { RootModelType } from '../RootModel';
import type { User } from '../Viewer/UserModel';
import { CommentParentEntityEnum } from '@constants';

export type CommentListParentEntityType = {
  id: number;
};

export class CommentList extends ExtendedListModel(
  CommentRefModel,
  {
    totalCount: 0,
  },
  {
    idAttribute: 'id',
    schema: schemas.CommentListSchema,
    pageSize: 20,
  },
) {
  get parentEntityName() {
    const name = getType(this.parentEntity).name;

    // This case should never happen
    if (
      !Object.values(CommentParentEntityEnum).includes(name as any)
    ) {
      console.error(
        // eslint-disable-next-line max-len
        'Seems that you are using comments for not configured entity, please take a look at CommentParentEntityEnum and getCommentApiByParentEntity',
      );
    }

    return name as CommentParentEntityEnum;
  }

  get parentEntity() {
    const parent = this._parent;

    if (CommentModel.is(parent)) {
      return parent.parentEntity;
    }

    return parent;
  }

  /**
   * Id of the parent entity to which the comment belongs can be Post, Tutorial, Track, etc.
   */
  get parentEntityId() {
    return this.parentEntity.id;
  }

  /**
   * Id of the parent comment, if there is an id this means that this comment list of replies
   * to someone's comment
   */
  get parentCommentId() {
    return this._parentComment?.id;
  }

  private get _CommentApi() {
    return getCommentApiByParentEntity(this.parentEntityName);
  }

  private get _parent() {
    return getParent<
      IModelType<any, Comment> | CommentListParentEntityType
    >(this);
  }

  private get _parentComment() {
    const parent = this._parent;

    if (CommentModel.is(parent)) {
      return parent;
    }

    return;
  }

  private get _viewer(): User {
    // TODO: add getViewer util
    return getRoot<RootModelType>(this).viewer.currentUser!;
  }

  private _setTotalCount(value: number) {
    this.totalCount = value;
  }

  private _incrementTotalCount() {
    this.totalCount = this.totalCount + 1;
  }

  private _decrementTotalCount() {
    this.totalCount = Math.max(this.totalCount - 1, 0);
  }

  removeItem(item: Comment) {
    this.removeById(item.id);
    this._decrementTotalCount();
  }

  // TODO: move this thunk into UserCollectionModel
  fetchUsersAndMerge = createThunk(
    (commentsResponse: ICommentResponse[]) => {
      return async function fetchUsersAndMerge(
        this: CommentList,
        flow,
      ) {
        const userIds = commentsResponse.map((c) => c.userId);

        if (userIds.length === 0) {
          return [];
        }

        const usersResponse = await Api.Profile.getSharedData(
          userIds,
        );

        const normalizedUsers = flow.normalize(
          usersResponse.data,
          schemas.UserListSchema,
        );

        return commentsResponse.map((comment) => ({
          ...comment,
          user: normalizedUsers.entities.users[
            comment.userId
          ] as IUserPublicResponse,
          comments: {
            totalCount: comment.comments,
          },
        }));
      };
    },
  );

  fetch = createThunk(() => {
    return async function fetch(this: CommentList) {
      const res = await this._CommentApi.getComments({
        id: this.parentEntityId,
        count: this.pageSize,
        start: 0,
        parentId: this.parentCommentId,
      });

      const comments = await this.fetchUsersAndMerge.run(
        res.data.items,
      );

      this.set(comments);
      this._setTotalCount(res.data.totalCount);
    };
  });

  fetchMore = createThunk(() => {
    return async function fetchMore(this: CommentList, flow) {
      if (shouldRejectPagination(this)) {
        return;
      }

      await handleFlow(flow, async () => {
        const res = await this._CommentApi.getComments({
          id: this.parentEntityId,
          count: this.pageSize,
          start: this.offset,
          parentId: this.parentCommentId,
        });

        const comments = await this.fetchUsersAndMerge.run(
          res.data.items,
        );
        this.append(castToSnapshot(comments));
      });
    };
  }, false);

  create = createThunk(
    (params: Pick<ICreateCommentParams, 'message'>) => {
      return async function create(this: CommentList) {
        const message = trim(params.message, true);

        // TODO: find a way to add types for this object
        const commentData = {
          id: Date.now(),
          userId: this._viewer.id,
          user: this._viewer,
          message,
          sendTime: new Date().toISOString(),
          comments: {
            totalCount: 0,
          },
          likes: 0,
          userStats: {
            isLiked: false,
          },
        };

        try {
          this.addToBegin(castToSnapshot(commentData));
          this._incrementTotalCount();

          const res = await this._CommentApi.createComment({
            id: this.parentEntityId,
            message,
            parentCommentId: this.parentCommentId,
          });

          this.replace(
            commentData.id,
            castToSnapshot({
              ...commentData,
              id: res.data.id,
            }),
          );
        } catch (error) {
          // Remove added to begin comment
          this.removeById(commentData.id);
          this._decrementTotalCount();

          throw error;
        }
      };
    },
  );
}

export const CommentListModel = model(CommentList);

export function applyCommentListModelSnapshotProcessor<
  T extends typeof CommentListModel,
>(commentListModel: T, defaultOptions = {}) {
  return types.snapshotProcessor(commentListModel, {
    preProcessor(snapshot: number | object) {
      if (typeof snapshot === 'number') {
        return {
          totalCount: snapshot,
          ...defaultOptions,
        };
      }

      return {
        ...snapshot,
        ...defaultOptions,
      };
    },
  });
}

export const ProcessedCommentListModel =
  applyCommentListModelSnapshotProcessor(CommentListModel);
